import { useState, useRef } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { callApi, handleErrorCommon } from "../common/api/Api";
import { Serial, searchLicense, addLicense } from "./SerialApi";
import { Option } from "../common/api/Model";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import { CommonDialog } from "./CommonDialog";
import { ErrorDialog } from "../common/components/ErrorDialog";
import Button from "@mui/material/Button";
import { isJp } from "../i18n/LangUtil";
import i18n from "../i18n/configs";
import { licenseNumberForView } from "../common/utils/Utils";

interface Props {
  onRegister: (serialList: Serial[]) => void;
  targetSerial: Serial;
  serialList: Serial[];
}

/**
 * ライセンス追加ボタンのコンポーネントです。
 *
 * @returns ライセンス追加ボタンのコンポーネント
 */
export const RegisterLicenseButton = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const { showBoundary } = useErrorBoundary();
  const [inputDialogOpen, setInputDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const firstLicenseNumberRef = useRef<HTMLInputElement>();
  const middleLicenseNumberRef = useRef<HTMLInputElement>();
  const lastLicenseNumberRef = useRef<HTMLInputElement>();
  const [inputLicenseNum, setInputLicenseNum] = useState<string>("");
  const [optionName, setOptionName] = useState<string>("");

  const inputContent = (
    <>
      <div className="grid grid-cols-7 items-center">
        <TextField
          inputRef={firstLicenseNumberRef}
          variant="outlined"
          margin="normal"
          type="text"
          size="medium"
          inputProps={{ maxLength: 4 }}
          fullWidth
          autoFocus
        />
        <div className="text-base text-center">{"ー"}</div>
        <TextField
          inputRef={middleLicenseNumberRef}
          variant="outlined"
          margin="normal"
          type="text"
          size="medium"
          inputProps={{ maxLength: 4 }}
          fullWidth
        />
        <div className="text-base text-center">{"ー"}</div>
        <TextField
          inputRef={lastLicenseNumberRef}
          variant="outlined"
          margin="normal"
          type="text"
          size="medium"
          inputProps={{ maxLength: 4 }}
          fullWidth
        />
      </div>
    </>
  );

  const onCloseErrorDialog = () => {
    setErrorDialogOpen(false);
  };

  const onConfirm = () => {
    if (!firstLicenseNumberRef?.current || !middleLicenseNumberRef?.current || !lastLicenseNumberRef?.current) return;

    const licenseNumber: string =
      firstLicenseNumberRef.current.value + middleLicenseNumberRef.current.value + lastLicenseNumberRef.current.value;
    setInputLicenseNum(licenseNumber);

    const resultPromise = searchLicense(licenseNumber);
    resultPromise.then(function (result) {
      if (result.error) {
        return handleErrorCommon(result.error?.response?.data, result.error, showBoundary, t);
      }
      if (!result.data) {
        setErrorMessage(t("serial.invalidLicenseErrorMessage"));
        setErrorDialogOpen(true);
      } else if (licenseNumber === result.data?.licenseNumber && result.data.serialId) {
        setErrorMessage(t("serial.existingLicenseErrorMessage"));
        setErrorDialogOpen(true);
      } else if (props.targetSerial.licenses.some((value) => value.optionId === result.data?.optionId)) {
        setErrorMessage(t("serial.duplicateLicenseOptionErrorMessage"));
        setErrorDialogOpen(true);
      } else {
        setOptionName(optName(result.data.option));
        setConfirmDialogOpen(true);
      }
    });
  };

  const optName = (opt: Option) => {
    if (isJp(i18n)) {
      return opt.nameJa;
    }
    return opt.nameEn;
  };

  const confirmContent = (
    <>
      <div className="text-base">{`${t("serial.confirmOptionNumberInfoMessage")}`}</div>
      <div className="text-base">{`${t("serial.confirmSerialNumberDuplicationInfoMessage")}`}</div>

      <ul className="p-2">
        <li>{`${t("common.serialNumberOfCameraLabel")} : ${props.targetSerial?.serialNumber}`}</li>
        <li>{`${t("serial.funcNameOfOptionLabel")} : ${optionName}`}</li>
        <li>{`${t("common.licenseNumber")} : ${licenseNumberForView(inputLicenseNum)}`}</li>
      </ul>
    </>
  );

  const onRegister = () => {
    if (!props.targetSerial || !props.serialList || !inputLicenseNum) {
      return;
    }

    setInputDialogOpen(false);
    setConfirmDialogOpen(false);
    setInputLicenseNum("");

    (async () => {
      await callApi(async () => {
        const result = await addLicense(props.targetSerial, inputLicenseNum);
        if (result.error) {
          return handleErrorCommon(result.error?.response?.data, result.error, showBoundary, t);
        }

        if (!result.data) {
          return;
        }
        const newSerialList: Serial[] = props.serialList.filter((value) => {
          return value.id !== result.data?.id;
        });
        newSerialList.push(result.data);
        newSerialList.sort((a, b) => a.id - b.id);
        props.onRegister(newSerialList);
      });
    })();
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={() => {
          setInputDialogOpen(true);
        }}
      >
        {t("serial.addOptionButton")}
      </Button>

      {/* 入力ダイアログ */}
      <CommonDialog
        open={inputDialogOpen}
        title={t("serial.inputLicenseNumberMessage")}
        acceptButtonName={t("common.informButton")}
        onAccept={onConfirm}
        closeButtonName={t("common.cancelButton")}
        onClose={() => {
          setInputDialogOpen(false);
          setInputLicenseNum("");
        }}
      >
        {inputContent}
      </CommonDialog>

      {/* 確認ダイアログ */}
      <CommonDialog
        open={confirmDialogOpen}
        title={t("serial.confirmOptionTitle")}
        acceptButtonName={t("common.registerButton")}
        onAccept={onRegister}
        closeButtonName={t("common.backButton")}
        onClose={() => {
          setConfirmDialogOpen(false);
        }}
      >
        {confirmContent}
      </CommonDialog>

      {/* エラーダイアログ */}
      <ErrorDialog open={errorDialogOpen} message={errorMessage} onAccept={onCloseErrorDialog} />
    </>
  );
};
